import { useTranslation } from "react-i18next";
import { useState } from "react";

export default function RegistrationSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    parentName: "",
    childName: "",
    childAge: "",
    email: "",
    phone: "",
    trialDate: "",
    japaneseLevel: "",
    additionalInfo: "",
    hearAboutUs: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch("https://readdy.ai/api/form/d4ub7uvsdr6u9jrifg9g", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          parentName: "",
          childName: "",
          childAge: "",
          email: "",
          phone: "",
          trialDate: "",
          japaneseLevel: "",
          additionalInfo: "",
          hearAboutUs: ""
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="register" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl sm:text-5xl font-bold text-center text-gray-900 mb-6">
          {t("registration_title")}
        </h2>
        <p className="text-xl text-gray-600 text-center mb-12">{t("registration_text")}</p>

        <form onSubmit={handleSubmit} className="bg-gradient-to-br from-teal-50 to-white rounded-3xl shadow-xl p-8 md:p-10" data-readdy-form>
          <div className="space-y-6">
            <div>
              <label htmlFor="parentName" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_parent_name")} *
              </label>
              <input
                type="text"
                id="parentName"
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="childName" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_child_name")} *
              </label>
              <input
                type="text"
                id="childName"
                name="childName"
                value={formData.childName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="childAge" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_child_age")} *
              </label>
              <select
                id="childAge"
                name="childAge"
                value={formData.childAge}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">{t("form_select_age")}</option>
                <option value="3">3 years</option>
                <option value="4">4 years</option>
                <option value="5">5 years</option>
                <option value="6">6 years</option>
                <option value="7">7 years</option>
                <option value="8">8 years</option>
                <option value="9">9 years</option>
                <option value="10">10 years</option>
              </select>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_email")} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_phone")}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="trialDate" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_trial_date")} *
              </label>
              <select
                id="trialDate"
                name="trialDate"
                value={formData.trialDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">{t("form_select_date")}</option>
                <option value="weekdays_3pm">{t("form_weekdays_3pm")}</option>
                <option value="saturday_2pm">{t("form_saturday_2pm")}</option>
                <option value="sunday_10am">{t("form_sunday_10am")}</option>
              </select>
            </div>

            <div>
              <label htmlFor="japaneseLevel" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_japanese_level")} *
              </label>
              <select
                id="japaneseLevel"
                name="japaneseLevel"
                value={formData.japaneseLevel}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">{t("form_select_level")}</option>
                <option value="no_experience">{t("form_level_no_experience")}</option>
                <option value="beginner">{t("form_level_beginner")}</option>
                <option value="can_speak_little">{t("form_level_can_speak_little")}</option>
                <option value="can_understand">{t("form_level_can_understand")}</option>
              </select>
            </div>

            <div>
              <label htmlFor="additionalInfo" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_additional_info")}
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
            </div>

            <div>
              <label htmlFor="hearAboutUs" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("form_hear_about_us")}
              </label>
              <input
                type="text"
                id="hearAboutUs"
                name="hearAboutUs"
                value={formData.hearAboutUs}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? t("form_submitting") : t("registration_cta")}
            </button>

            {submitStatus === "success" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 font-medium">{t("form_success")}</p>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-red-800 font-medium">{t("form_error")}</p>
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
